import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";
import type { UnknownRecord } from "../../types/common";

export interface ReconciliationRunRow {
  run_id: string;
  run_kind: string;
  started_at: string;
  completed_at?: string | null;
  approved: boolean;
  approved_by?: string | null;
  approved_at?: string | null;
  trade_date?: string | null;
  payload?: UnknownRecord | null;
  notes?: string | null;
  error_detail?: string | null;
}

export interface BookingFailureRow {
  id: string;
  venue: string;
  venue_trade_id: string;
  instrument_id?: string | null;
  error_kind: string;
  error_detail: string;
  raw_payload?: unknown | null;
  failed_at: string;
  acknowledged_at?: string | null;
  acknowledged_by?: string | null;
  replay_trade_id?: string | null;
  last_edited_at?: string | null;
  last_edited_by?: string | null;
  mo_notes?: string | null;
  replay_attempts: number;
}

export interface BookingFailurePatch {
  raw_payload?: unknown;
  mo_notes?: string;
}

export const BOOKING_FAILURES_KEY = ["ops", "booking-failures"] as const;

/** Booking failures — refetched every 30 s for alert badge freshness. */
export function useBookingFailures(opts?: { refetchInterval?: number }) {
  return useQuery({
    queryKey: BOOKING_FAILURES_KEY,
    queryFn: () => api.get<BookingFailureRow[]>("/ops/booking-failures"),
    refetchInterval: opts?.refetchInterval ?? 30_000,
    staleTime: 20_000,
  });
}

export interface ReplayBookingFailureResult {
  booking_failure_id: string;
  outcome: "BOOKED" | "FAILED" | "ALREADY_EXISTS" | string;
  trade_id?: string | null;
  detail?: string | null;
  nats_publish?: boolean;
  nats_subject?: string;
}

export function useReplayBookingFailure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<ReplayBookingFailureResult>(
        `/ops/booking-failures/${encodeURIComponent(id)}/replay`,
      ),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: BOOKING_FAILURES_KEY }); },
  });
}

export interface BatchReplayBookingFailureResult {
  results: Array<{
    id: string;
    venue_trade_id: string;
    outcome: string;
    trade_id?: string | null;
    detail?: string | null;
  }>;
  booked: number;
  already_exists: number;
  failed: number;
  skipped: number;
}

export function useBatchReplayBookingFailures() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      ids?: string[];
      error_kind?: string;
      auto_patch?: boolean;
      dry_run?: boolean;
    }) => api.post<BatchReplayBookingFailureResult>("/ops/booking-failures/batch-replay", body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: BOOKING_FAILURES_KEY }); },
  });
}

export function usePatchBookingFailure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: BookingFailurePatch }) =>
      api.patch(`/ops/booking-failures/${encodeURIComponent(id)}`, patch),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: BOOKING_FAILURES_KEY }); },
  });
}

export function useAcknowledgeBookingFailure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post(`/ops/booking-failures/${encodeURIComponent(id)}/acknowledge`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: BOOKING_FAILURES_KEY }); },
  });
}

export function useReconciliationRuns(kind?: string) {
  const q = kind ? `?${new URLSearchParams({ run_kind: kind }).toString()}` : "";
  return useQuery({
    queryKey: ["ops", "recon", "runs", q],
    queryFn: () => api.get<ReconciliationRunRow[]>(`/ops/reconciliation/runs${q}`),
  });
}

export interface ReconciliationCorrectiveRow {
  id: string;
  run_id: string;
  trade_id: string;
  idempotency_hash: string;
  break_id?: string | null;
  instrument_id?: string | null;
  approved: boolean;
  approved_by?: string | null;
  approved_at?: string | null;
  created_at: string;
  notes?: string | null;
}

export function useRunCorrectives(runId: string | null | undefined) {
  return useQuery({
    queryKey: ["ops", "recon", "correctives", runId ?? "none"],
    enabled: Boolean(runId),
    queryFn: () =>
      api.get<ReconciliationCorrectiveRow[]>(
        `/ops/reconciliation/runs/${encodeURIComponent(runId!)}/correctives`,
      ),
  });
}

export interface ReconciliationScheduleRow {
  id: number;
  enabled: boolean;
  mode: "INTERVAL" | "CRON" | string;
  interval_seconds: number;
  cron_expr?: string | null;
  timezone: string;
  next_run_at?: string | null;
  updated_at: string;
  updated_by?: string | null;
}

export function useReconcileSchedule() {
  return useQuery({
    queryKey: ["ops", "recon", "schedule"],
    queryFn: () => api.get<ReconciliationScheduleRow>("/ops/reconciliation/schedule"),
  });
}

/** Queues a reconciliation pass; the position-reconciler picks it up on its next poll. */
export function useReconciliationTrigger() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ invoke_id: string; status: string }>(`/ops/reconciliation/trigger`, {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["ops", "recon"] }); },
  });
}
